import { useNavigate } from 'react-router-dom';
import { skipToken, useMutation, useSuspenseQuery } from '@apollo/client';
import { useFragment } from '../../apollo/__generated__';
import {
  CREATE_TEST,
  GET_TEST_TYPES,
  TEST_CORE_FRAGMENT,
  UPDATE_TEST,
} from '../../apollo/tests.ts';
import { useEffect } from 'react';
import Button from '../common/button.tsx';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import RadioButton from '../common/radio-button.tsx';
import {
  Maybe,
  TestFragmentFragment,
} from '../../apollo/__generated__/graphql.ts';

interface IForm {
  name: string;
  description?: string;
  notes?: string;
  typeId: number;
}
interface IProps {
  test?: Maybe<TestFragmentFragment>;
  onCloseModal?: (slug: string) => void;
  editField?: 'name' | 'description' | 'notes';
}
const TEST_TYPE_ORDER = ['SAT Test', 'SAT Reading/Writing', 'SAT Math'];

const sortTestTypes = <T extends { name: string }>(types: T[]): T[] =>
  [...types].sort((a, b) => {
    const ai = TEST_TYPE_ORDER.indexOf(a.name);
    const bi = TEST_TYPE_ORDER.indexOf(b.name);
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

const CreateTestForm = ({ test, onCloseModal, editField }: IProps) => {
  const navigate = useNavigate();

  const { data } = useSuspenseQuery(
    GET_TEST_TYPES,
    test ? skipToken : { errorPolicy: 'all' },
  );
  const [createTest, { data: newTestData }] = useMutation(CREATE_TEST);
  const [updateTest] = useMutation(UPDATE_TEST);

  const newTest = useFragment(TEST_CORE_FRAGMENT, newTestData?.test.create);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<IForm>({
    defaultValues: {
      name: test?.name ? test.name : undefined,
      description: test?.description ?? '',
      notes: test?.notes ?? '',
      typeId: test?.type.id ? test.type.id : sortTestTypes(data?.test?.testTypes ?? [])[0]?.id,
    },
  });

  const testType = watch('typeId');

  useEffect(() => {
    const slug = newTest?.slug;
    if (slug) navigate(`/tests-browser/${slug}`);
  }, [navigate, newTest]);

  const onCreate: SubmitHandler<IForm> = (data) => {
    const create = createTest({
      variables: {
        input: {
          name: data.name,
          description: data.description?.trim() || undefined,
          notes: data.notes?.trim() || undefined,
          typeId: +data.typeId,
        },
      },
    });

    toast.promise(create, {
      pending: 'Creating test...',
      success: 'Test created',
    });
  };

  const onUpdate: SubmitHandler<IForm> = (data) => {
    if (!test?.id) return;
    const update = updateTest({
      variables: {
        input: {
          id: test.id,
          name: data.name,
          description: data.description?.trim() || undefined,
          notes: data.notes?.trim() || undefined,
          status: test.status,
        },
      },
    });

    toast.promise(update, {
      pending: 'Updating test...',
      success: 'Test updated',
    });

    update.then((data) => {
      const slug = data.data?.test.update.slug;
      if (onCloseModal && slug) onCloseModal(slug);
    });
  };

  return (
    <div className={'w-[400px] p-4'}>
      {!test && (
        <>
          <h1>Create new Test</h1>
          <div className={'mt-4 grid grid-cols-1 gap-3'}>
            {sortTestTypes(data?.test.testTypes ?? []).map((type) => {
              return (
                <RadioButton
                  key={type.name}
                  label={type.name}
                  value={type.id}
                  checked={Number(testType) === type.id}
                  register={register('typeId')}
                />
              );
            })}
          </div>
        </>
      )}

      {(!test || editField === 'name' || !editField) && (
        <div className={'mt-4'}>
          <label htmlFor="test-name">Name</label>
          <input
            type="text"
            className={'base-input'}
            placeholder={'e.g. Math Test'}
            {...register('name', { required: 'This field is required' })}
          />
          <p className={'h-4 text-sm text-red'}>
            {errors.name ? errors.name.message : ''}
          </p>
        </div>
      )}

      {(!test || editField === 'description' || !editField) && (
        <div className={'mt-2'}>
          <label htmlFor="test-description">Description</label>
          <textarea
            id="test-description"
            className={'base-input min-h-24 resize-y'}
            placeholder={'Add a short description for this test'}
            {...register('description')}
          />
        </div>
      )}

      {(!test || editField === 'notes' || !editField) && (
        <div className={'mt-2'}>
          <label htmlFor="test-notes">Notes</label>
          <textarea
            id="test-notes"
            className={'base-input min-h-24 resize-y'}
            placeholder={'Add internal notes for this test'}
            {...register('notes')}
          />
        </div>
      )}

      <Button
        type={'button'}
        onClick={test ? handleSubmit(onUpdate) : handleSubmit(onCreate)}
        className={'mt-8 w-full'}
      >
        {test ? 'Save' : 'Create'}
      </Button>
    </div>
  );
};
export default CreateTestForm;
